import type { ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { ComboboxPopup } from './combobox/ComboboxPopup';
import { useCombobox } from './combobox/useCombobox';

export type TextComboboxProps<T> = {
  id: string;
  value: string;
  onChange: (value: string) => void;
  /** Rows for the current value, already filtered and sorted by the caller. */
  options: readonly T[];
  /** The value a row stands for: picking it puts this text in the field. */
  optionValue: (option: T) => string;
  optionKey: (option: T) => string;
  renderOption: (option: T) => ReactNode;
  /** Small caption above the rows ("Références existantes · 11"). */
  header?: ReactNode;
  /** Line under the rows ("et 12 autres"). */
  footer?: ReactNode;
  emptyLabel: string;
  /** Enter with no row to pick, or on the row that already matches the value. */
  onEnter?: () => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  inputClassName?: string;
  describedBy?: string;
  invalid?: boolean;
  /** Extra row classes, e.g. to set the "create" row apart. */
  optionClassName?: (option: T) => string | undefined;
  /** No panel at all when no row matches (plain suggestions). */
  hideWhenEmpty?: boolean;
  /** Accessible name when the field has no visible `<label>`. */
  ariaLabel?: string;
};

/**
 * Free-text field with a list of suggestions (WAI-ARIA combobox, list autocomplete). Typing
 * filters the rows, ↑ ↓ move, Enter picks the highlighted row, Escape closes. The value is
 * whatever is typed or picked.
 */
export function TextCombobox<T>({
  id,
  value,
  onChange,
  options,
  optionValue,
  optionKey,
  renderOption,
  header,
  footer,
  emptyLabel,
  onEnter,
  placeholder,
  required,
  disabled,
  inputClassName,
  describedBy,
  invalid,
  optionClassName,
  hideWhenEmpty = false,
  ariaLabel,
}: TextComboboxProps<T>) {
  const combobox = useCombobox<T>({
    id,
    options,
    onPick: (option) => onChange(optionValue(option)),
    onEnter,
    // Enter on the row that already holds the value submits instead of re-picking it.
    shouldPick: (option) => optionValue(option) !== value.trim(),
  });
  const expanded = combobox.open && !disabled && !(hideWhenEmpty && options.length === 0);

  return (
    <div ref={combobox.containerRef} className="relative">
      <Input
        id={id}
        type="text"
        role="combobox"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={expanded}
        aria-controls={combobox.listboxId}
        aria-activedescendant={
          expanded && combobox.activeIndex >= 0
            ? combobox.optionId(combobox.activeIndex)
            : undefined
        }
        aria-describedby={describedBy}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        value={value}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className={cn('pr-9', inputClassName)}
        onChange={(event) => {
          onChange(event.target.value);
          combobox.setOpen(true);
          combobox.setActive(event.target.value ? 0 : -1);
        }}
        onFocus={() => combobox.setOpen(true)}
        onClick={() => combobox.setOpen(true)}
        onKeyDown={combobox.onKeyDown}
      />
      <ChevronDown
        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      {expanded && (
        <ComboboxPopup
          combobox={combobox}
          options={options}
          optionKey={optionKey}
          renderOption={renderOption}
          optionClassName={optionClassName}
          emptyLabel={emptyLabel}
          header={header}
          footer={footer}
        />
      )}
    </div>
  );
}
